import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { resolveTenantDomain, onboardTenant } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from '../utils/demoMode';

// Onboarding is a write flow (blocked in Demo Mode at the API guard); the consent
// URL would also carry a masked tenant id, so copying it is disabled too.
const DEMO = isDemoMode();

const STEPS = ['Tenant ID', 'Configure', 'Admin Consent', 'Submit'];

const LICENSE_TIERS = [
  { value: 'Basic', label: 'Basic', description: '6 workers — Activity Feed + Directory Audits (any license)' },
  { value: 'Identity', label: 'Identity', description: '9 workers — Basic + Risk Detections, Risky Users, CA Policies (Entra ID P1/P2)' },
  { value: 'Defender', label: 'Defender', description: '12 workers — Identity + Security Alerts, Incidents, Secure Score (E5, Business Premium, or Defender add-ons)' },
];

// Microsoft cloud the tenant lives in. Blank is equivalent to Commercial. Set by an operator —
// never auto-detected from licensing. Log collection uses this to skip feeds Microsoft doesn't
// support in government clouds (today: message trace, unsupported in GCC).
const CLOUD_ENVIRONMENTS = [
  { value: 'Commercial', label: 'Commercial', description: 'Normal Microsoft 365 commercial cloud — the default for almost every tenant' },
  { value: 'GCC', label: 'GCC', description: 'US Government Community Cloud — message trace is not available (Microsoft limitation)' },
  { value: 'GCCHigh', label: 'GCC High', description: 'US Government Community Cloud High' },
  { value: 'DoD', label: 'DoD', description: 'US Department of Defense cloud' },
];


export default function OnboardingDrawer({ isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const adminMspCodes = user?.adminMspCodes || [];
  const mspCatalog = user?.mspCatalog || [];

  // MSP options for the dropdown: global admins choose any catalog MSP; MSP admins
  // are restricted to the MSP(s) they administer. Falls back to raw admin codes when
  // the catalog is empty/missing those codes, and a free-text input (below) when no
  // catalog exists for a global admin (dev safety).
  let mspOptions;
  if (isGlobalAdmin) {
    mspOptions = mspCatalog;
  } else if (mspCatalog.length > 0) {
    const set = new Set(adminMspCodes.map((c) => (c || '').toUpperCase()));
    mspOptions = mspCatalog.filter((m) => set.has((m.code || '').toUpperCase()));
    if (mspOptions.length === 0) mspOptions = adminMspCodes.map((c) => ({ code: c, displayName: c }));
  } else {
    mspOptions = adminMspCodes.map((c) => ({ code: c, displayName: c }));
  }
  const defaultMsp = isGlobalAdmin ? 'PFIT' : (adminMspCodes[0] || '');

  const [step, setStep] = useState(0);

  // Step 1: Tenant ID
  const [tenantId, setTenantId] = useState('');
  const [domain, setDomain] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [tenantExists, setTenantExists] = useState(false);
  const [resolved, setResolved] = useState(false);

  // Step 2: Configuration
  const [licenseTier, setLicenseTier] = useState('');
  const [cloudEnvironment, setCloudEnvironment] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [shortName, setShortName] = useState('');
  const [msp, setMsp] = useState(defaultMsp);
  const [azureSubIds, setAzureSubIds] = useState('');

  // Keep `msp` pointed at a real, selectable option. The drawer mounts with
  // TenantManagement (always rendered, not gated on isOpen), so `useState(defaultMsp)`
  // can snapshot an empty default before /api/me has populated user.adminMspCodes —
  // and that initial value never re-applies on later renders. A native <select> only
  // *displays* its first option when `value` matches nothing; it never writes that
  // back to state, so the empty `msp` survived and both the review screen and the
  // submit body fell through to the hardcoded 'PFIT' default (rejected server-side
  // for non-global MSP admins). This re-syncs `msp` once the options are known.
  useEffect(() => {
    if (!isOpen) return;
    if (mspOptions.length > 0 && !mspOptions.some((m) => m.code === msp)) {
      const preferred = mspOptions.some((m) => m.code === defaultMsp)
        ? defaultMsp
        : mspOptions[0].code;
      setMsp(preferred);
    }
  }, [isOpen, mspOptions, msp, defaultMsp]);

  // Step 4: Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Consent URL copy state
  const [copied, setCopied] = useState(false);
  const [copiedPsCmd, setCopiedPsCmd] = useState(false);
  const [copiedAuditCmd, setCopiedAuditCmd] = useState(false);

  const resetForm = () => {
    setStep(0);
    setTenantId('');
    setDomain('');
    setResolving(false);
    setResolveError(null);
    setTenantExists(false);
    setResolved(false);
    setLicenseTier('');
    setCloudEnvironment('');
    setFriendlyName('');
    setShortName('');
    setMsp(defaultMsp);
    setAzureSubIds('');
    setSubmitting(false);
    setSubmitError(null);
    setCopied(false);
    setCopiedPsCmd(false);
    setCopiedAuditCmd(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Step 1: Resolve domain to tenant ID
  const handleResolveDomain = async () => {
    if (!domain.trim()) return;
    setResolving(true);
    setResolveError(null);
    setTenantExists(false);
    setResolved(false);
    try {
      const result = await resolveTenantDomain(domain.trim());
      setTenantId(result.tenantId);
      setTenantExists(result.exists);
      setResolved(true);
    } catch (err) {
      setResolveError(err.message);
    } finally {
      setResolving(false);
    }
  };

  // Step 1 validation
  const step1Valid = tenantId.trim().length > 0 && domain.trim().length > 0 && !tenantExists;

  // Step 2 validation — license tier is optional (detected later via probe)
  const step2Valid = true;

  // Step 4: Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onboardTenant({
        tenantId: tenantId.trim(),
        domain: domain.trim(),
        friendlyName: friendlyName.trim() || domain.split('.')[0],
        shortName: shortName.trim() || domain.split('.')[0].substring(0, 5).toUpperCase(),
        msp: msp.trim() || 'PFIT',
        licenseTier: licenseTier || undefined,
        cloudEnvironment: cloudEnvironment || undefined,
        azureSubIds: azureSubIds.trim(),
      });
      onSuccess?.();
      handleClose();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const consentUrl = tenantId
    ? `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=0c9e2aab-0d6f-4bf4-a90a-d3c87727fefa&redirect_uri=${encodeURIComponent(`${appUrl}/consent-complete.html`)}`
    : '';

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const auditLogCmd = 'Set-AdminAuditLogConfig -UnifiedAuditLogIngestionEnabled $true';

  const handleCopyAuditCmd = async () => {
    try {
      await navigator.clipboard.writeText(auditLogCmd);
      setCopiedAuditCmd(true);
      setTimeout(() => setCopiedAuditCmd(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const exchangeSpCmd = 'New-MgServicePrincipal -AppId "8bd644d1-64a1-4d4b-ae52-2e0cbf64e373"';

  const handleCopyPsCmd = async () => {
    try {
      await navigator.clipboard.writeText(exchangeSpCmd);
      setCopiedPsCmd(true);
      setTimeout(() => setCopiedPsCmd(false), 2000);
    } catch { /* clipboard not available */ }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-gray-100">Onboard New Tenant</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step Indicators */}
        <div className="px-6 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    i < step ? 'bg-blue-600 text-white' :
                    i === step ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' :
                    'bg-gray-800 text-gray-500'
                  }`}>
                    {i < step ? <CheckIcon className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span className={`text-xs ${i === step ? 'text-gray-200' : 'text-gray-500'}`}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-800" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Step 1: Tenant ID ── */}
          {step === 0 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tenant ID <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={tenantId}
                  onChange={(e) => { setTenantId(e.target.value); setTenantExists(false); }}
                  placeholder="e.g., ab4020eb-eb53-42a7-abd2-a424143882ad"
                  className="input-field font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the client's Azure AD tenant ID (GUID), or use the domain lookup below.
                </p>
              </div>

              <div className="border-t border-gray-800 pt-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Domain <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => { setDomain(e.target.value); setResolved(false); setResolveError(null); }}
                    placeholder="e.g., westclinic.com"
                    className="input-field flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && handleResolveDomain()}
                  />
                  <button
                    onClick={handleResolveDomain}
                    disabled={!domain.trim() || resolving}
                    className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                  >
                    {resolving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300" />
                    ) : (
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    )}
                    Lookup
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Resolves the domain via Microsoft's public OpenID endpoint and populates the Tenant ID field above.
                </p>
              </div>

              {resolved && !tenantExists && (
                <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                  <p className="text-sm text-green-300">Domain resolved successfully. Tenant ID populated above.</p>
                </div>
              )}

              {tenantExists && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-300">This tenant already exists in the configuration.</p>
                    <p className="text-xs text-amber-400/70 mt-1">You cannot onboard a duplicate tenant.</p>
                  </div>
                </div>
              )}

              {resolveError && (
                <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{resolveError}</p>
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Configure ── */}
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  License Tier <span className="text-gray-600">(optional)</span>
                </label>
                <select
                  value={licenseTier}
                  onChange={(e) => setLicenseTier(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select a tier...</option>
                  {LICENSE_TIERS.map((tier) => (
                    <option key={tier.value} value={tier.value}>{tier.label}</option>
                  ))}
                </select>
                {licenseTier && (
                  <p className="mt-1 text-xs text-gray-500">
                    {LICENSE_TIERS.find((t) => t.value === licenseTier)?.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Cloud Environment <span className="text-gray-600">(optional)</span>
                </label>
                <select
                  value={cloudEnvironment}
                  onChange={(e) => setCloudEnvironment(e.target.value)}
                  className="input-field"
                >
                  <option value="">Commercial (default)</option>
                  {CLOUD_ENVIRONMENTS.map((env) => (
                    <option key={env.value} value={env.value}>{env.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {cloudEnvironment
                    ? CLOUD_ENVIRONMENTS.find((e) => e.value === cloudEnvironment)?.description
                    : 'Leave as Commercial unless this is a US government cloud tenant. Some feeds are unavailable in government clouds, and setting this correctly prevents false collection alerts.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Friendly Name <span className="text-gray-600">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={friendlyName}
                    onChange={(e) => setFriendlyName(e.target.value)}
                    placeholder={domain ? domain.split('.')[0] : 'e.g., West Clinic'}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Short Name <span className="text-gray-600">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={shortName}
                    onChange={(e) => setShortName(e.target.value)}
                    placeholder={domain ? domain.split('.')[0].substring(0, 5).toUpperCase() : 'e.g., WCC'}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  MSP {isGlobalAdmin && <span className="text-gray-600">(default: PFIT)</span>}
                </label>
                {mspOptions.length > 0 ? (
                  <select
                    value={msp}
                    onChange={(e) => setMsp(e.target.value)}
                    className="input-field"
                  >
                    {mspOptions.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.displayName ? `${m.displayName} (${m.code})` : m.code}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={msp}
                    onChange={(e) => setMsp(e.target.value)}
                    className="input-field"
                  />
                )}
                {!isGlobalAdmin && (
                  <p className="mt-1 text-xs text-gray-500">
                    Scoped to the MSP{adminMspCodes.length > 1 ? '(s)' : ''} you administer.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Azure Subscription IDs <span className="text-gray-600">(optional, semicolon-delimited)</span>
                </label>
                <input
                  type="text"
                  value={azureSubIds}
                  onChange={(e) => setAzureSubIds(e.target.value)}
                  placeholder="e.g., 8f055744-...;98788b22-..."
                  className="input-field font-mono text-sm"
                />
              </div>

            </>
          )}

          {/* ── Step 3: Admin Consent ── */}
          {step === 2 && (
            <>
              <div className="p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg space-y-3">
                <p className="text-sm text-blue-300 font-medium">Send this Client specific URL to the client's M365 Global Admin:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={consentUrl}
                    readOnly
                    className="input-field flex-1 font-mono text-xs"
                  />
                  <button
                    onClick={handleCopyUrl}
                    disabled={DEMO}
                    title={DEMO ? DEMO_UNAVAILABLE_TITLE : undefined}
                    className="btn-secondary flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copied ? <CheckIcon className="h-4 w-4 text-green-400" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Prerequisite Notices */}
              <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-300">Prerequisite: Enable Unified Audit Logging</p>
                    <p className="text-xs text-amber-400/80 mt-1">
                      The client's Global Admin must ensure that <strong className="text-amber-300">Unified Audit Logging</strong> is
                      enabled in the Microsoft 365 tenant. This can be verified in the Microsoft Purview compliance portal
                      under <strong className="text-amber-300">Audit</strong>. Without this, the Activity Feed will not contain any events.
                    </p>
                    <div className="mt-2 relative">
                      <pre className="text-xs font-mono bg-gray-900/80 border border-gray-700 rounded p-3 text-gray-300 whitespace-pre-wrap break-all">
{auditLogCmd}
                      </pre>
                      <button
                        onClick={handleCopyAuditCmd}
                        className="absolute top-2 right-2 p-1 rounded bg-gray-800 hover:bg-gray-700 transition-colors"
                        title="Copy command"
                      >
                        {copiedAuditCmd ? (
                          <CheckIcon className="h-3.5 w-3.5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-300">Prerequisite: Register Exchange Service Principal</p>
                    <p className="text-xs text-amber-400/80 mt-1">
                      The Global Admin must run this PowerShell command <strong className="text-amber-300">once per tenant</strong> to
                      make the Exchange message trace endpoint available:
                    </p>
                    <div className="mt-2 relative">
                      <pre className="text-xs font-mono bg-gray-900/80 border border-gray-700 rounded p-3 text-gray-300 whitespace-pre-wrap break-all">
{exchangeSpCmd}
                      </pre>
                      <button
                        onClick={handleCopyPsCmd}
                        className="absolute top-2 right-2 p-1 rounded bg-gray-800 hover:bg-gray-700 transition-colors"
                        title="Copy command"
                      >
                        {copiedPsCmd ? (
                          <CheckIcon className="h-3.5 w-3.5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-300">Instructions for the client admin:</h3>
                <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                  <li>Open the URL above in a browser</li>
                  <li>Sign in with their <strong className="text-gray-300">Global Administrator</strong> account</li>
                  <li>Review the permissions listed for <strong className="text-gray-300">"Blue365Signal - Security Monitoring"</strong></li>
                  <li>Click <strong className="text-gray-300">Accept</strong></li>
                  <li>A confirmation page will appear showing <strong className="text-gray-300">"Consent Granted Successfully"</strong> — they can close the tab</li>
                </ol>
              </div>

              <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                <p className="text-xs text-amber-300">
                  You do not need to wait for consent before proceeding. The tenant will be created in <strong>Pending</strong> state.
                  Use the <strong>Activate</strong> button on the tenant list after the admin confirms consent.
                </p>
              </div>
            </>
          )}

          {/* ── Step 4: Submit ── */}
          {step === 3 && (
            <>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Review &amp; Confirm</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Tenant ID</span>
                  <span className="text-gray-200 font-mono text-xs">{tenantId}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Domain</span>
                  <span className="text-gray-200">{domain || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Friendly Name</span>
                  <span className="text-gray-200">{friendlyName || domain?.split('.')[0] || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Short Name</span>
                  <span className="text-gray-200">{shortName || domain?.split('.')[0]?.substring(0, 5).toUpperCase() || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">MSP</span>
                  <span className="text-gray-200">{msp || 'PFIT'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">License Tier</span>
                  <span className="text-gray-200">{LICENSE_TIERS.find((t) => t.value === licenseTier)?.label || licenseTier || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Cloud Environment</span>
                  <span className="text-gray-200">
                    {CLOUD_ENVIRONMENTS.find((e) => e.value === cloudEnvironment)?.label || 'Commercial'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-500">Auth Method</span>
                  <span className="text-gray-200">Federated Identity</span>
                </div>
                {azureSubIds && (
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-500">Azure Subs</span>
                    <span className="text-gray-200 font-mono text-xs truncate max-w-[200px]">{azureSubIds}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Initial Status</span>
                  <span className="text-amber-400 font-medium">Pending</span>
                </div>
              </div>

              <div className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg mt-4">
                <p className="text-xs text-gray-400">
                  The tenant will be created with log collection <strong className="text-gray-300">disabled</strong>.
                  After the client admin completes consent, use the <strong className="text-gray-300">Activate</strong> button
                  to start Activity Feed subscriptions and enable collection.
                </p>
              </div>

              {/* Post-onboarding follow-up actions */}
              <div className="p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg mt-4 space-y-3">
                <p className="text-sm font-medium text-blue-300">After submitting, complete these follow-up steps:</p>
                <ul className="text-xs text-blue-200/90 space-y-3 list-disc list-inside">
                  <li>
                    If this client is within <strong className="text-blue-200">DRMM</strong>, add a DRMM Site variable
                    of <strong className="text-blue-200">M365_Tenant_ID</strong> with value:
                    <div className="mt-1.5 ml-5 flex items-center gap-2">
                      <code className="font-mono text-xs bg-gray-900/80 border border-gray-700 rounded px-2 py-1 text-gray-300 break-all">
                        {tenantId || '—'}
                      </code>
                    </div>
                  </li>
                  <li>
                    If this client is in an accessible <strong className="text-blue-200">BlackPoint Cyber</strong> instance,
                    update Blue365Signal's <strong className="text-blue-200">"BlackPoint Cyber MDR Tenants"</strong> page with
                    the tenant-to-client mapping.
                  </li>
                </ul>
              </div>

              {submitError && (
                <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-300">Failed to create tenant</p>
                      <p className="text-xs text-red-400 mt-1">{submitError.split(' (')[0]}</p>
                    </div>
                  </div>
                  {submitError.includes('(') && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-400/70 cursor-pointer hover:text-red-300 transition-colors">
                        Show details
                      </summary>
                      <pre className="mt-1 text-xs text-red-400/80 bg-red-900/30 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                        {submitError}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : handleClose()}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 0 && !step1Valid) ||
                (step === 1 && !step2Valid)
              }
              className="btn-primary flex items-center gap-2"
            >
              Next
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
